/**
 * Configuration Service
 * 
 * Fetches public configuration from backend API.
 * This replaces build-time environment variables with dynamic configuration.
 */

import { API_ORIGIN_URL } from '../api/config';

interface PublicConfig {
  stripe: {
    publishable_key: string;
  };
  analytics: {
    ga_measurement_id: string;
    posthog_project_token: string;
    posthog_host: string;
  };
  auth0: {
    domain: string;
    frontend_client_id: string;
    audience: string;
  };
  environment: string;
  version: string;
}

interface ConfigResponse {
  success: boolean;
  data: PublicConfig;
}

class ConfigService {
  private config: PublicConfig | null = null;
  private isLoading = false;
  private loadPromise: Promise<PublicConfig> | null = null;

  /**
   * Get public configuration from backend
   * Uses caching to avoid multiple API calls
   */
  async getConfig(): Promise<PublicConfig> {
    // Return cached config if available
    if (this.config) {
      return this.config;
    }

    // Return existing promise if already loading
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.fetchConfig();
    
    try {
      this.config = await this.loadPromise;
      return this.config;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  private async fetchConfig(): Promise<PublicConfig> {
    try {
      const response = await fetch(`${API_ORIGIN_URL}/api/public-config/`);
      
      if (!response.ok) {
        throw new Error(`Config API returned ${response.status}`);
      }

      const result: ConfigResponse = await response.json();
      
      if (!result.success) {
        throw new Error('Config API returned success: false');
      }

      console.log('✅ [ConfigService] Public configuration loaded from backend');
      return result.data;
      
    } catch (error) {
      console.error('[ConfigService] Failed to load public configuration:', error);
      
      // Return fallback configuration
      return {
        stripe: { publishable_key: '' },
        analytics: { ga_measurement_id: '', posthog_project_token: '', posthog_host: 'https://us.i.posthog.com' },
        auth0: {
          domain: 'dev-0j52wd0nucdjxvp3.us.auth0.com',
          frontend_client_id: 'nopd8yLJuKWFQtyYgBeooTOTbSYGRwnU',
          audience: 'https://gal_001_os.bedrock.computer'
        },
        environment: 'development',
        version: '1.0'
      };
    }
  }

  /**
   * Get specific configuration values
   */
  async getStripePublishableKey(): Promise<string> {
    const config = await this.getConfig();
    return config.stripe.publishable_key;
  }

  async getGAMeasurementId(): Promise<string> {
    const config = await this.getConfig();
    return config.analytics.ga_measurement_id;
  }

  async getPostHogProjectToken(): Promise<string> {
    const config = await this.getConfig();
    return config.analytics.posthog_project_token;
  }

  async getPostHogHost(): Promise<string> {
    const config = await this.getConfig();
    return config.analytics.posthog_host || 'https://us.i.posthog.com';
  }

  /**
   * Clear cached configuration (useful for testing)
   */
  clearCache(): void {
    this.config = null;
    console.log('[ConfigService] Configuration cache cleared');
  }
}

// Export singleton instance
export const configService = new ConfigService();
export default configService;
